import { subject as tag } from '@casl/ability'
import { AbilityProvider, useAbility } from '@casl/react'
import { useQuery, useQueryClient } from '@tanstack/react-query'
import { type AppAbility, type AppId, apps, defineAbility } from '@tnn/core'
import { opensApp, subjects } from '@tnn/domain'
import { createContext, type ReactNode, useContext, useEffect, useMemo, useRef } from 'react'
import { Navigate, useLocation } from 'react-router'
import { api, isApiError, isTransient } from '../api/client'
import { loginUrl } from '../app/links'
import { ErrorScreen, LeaveFor, NoAccess, Splash } from '../app/states'
import type { Preferences } from '../prefs/preferences'
import { forgetPushSubscription } from '../pwa/subscription'
import { authClient, meQueryKey, refreshMeOnUnauthorized } from './client'
import { PinOver, PinScreen, usePinWatch } from './Pin'
import { ViewAsBadge } from './ViewAs'

export { authClient, meQueryKey }

export interface Me {
  user: { id: string; name: string; username: string; email: string | null; image: string | null }
  roles: { id: string; key: string; name: string }[]
  permissions: string[]
  driverId: string | null
  /** The employee behind the account: none for someone not on the staff (a contract driver) */
  employeeId: string | null
  /** Home branch (admin.user.branchId): new waybills start there */
  branchId: string | null
  demo: boolean
  preferences: Preferences
  /** This session's PIN (Pin.tsx): set, and the session waiting for it (nothing else answers until it is typed) */
  pin: { set: boolean; locked: boolean } | null
  /** A security deposit held, or a target set to build one: they see their money bag without holding cash */
  holdsBond: boolean
  /** A super admin looking at the apps as this person, read only (`ViewAsBadge`): who, and until when */
  viewedBy: { name: string; until: string } | null
}

interface SessionValue {
  me: Me
  /** The app running: shared modules (cash) link to pages of other apps from here */
  appId: AppId
  ability: AppAbility
  signOut: () => Promise<void>
}

const SessionContext = createContext<SessionValue | null>(null)

export function useMeQuery() {
  return useQuery({
    queryKey: meQueryKey,
    queryFn: () => api.get<Me>('/me'),
    // Ride out a short outage (about 15 s) before showing "cannot reach server"; 401 goes to sign-in at once
    retry: (count, error) => isTransient(error) && count < 4,
    retryDelay: (count) => Math.min(1000 * 2 ** count, 8000),
    staleTime: 5 * 60_000,
  })
}

export function useSession(): SessionValue {
  const value = useContext(SessionContext)
  if (!value) throw new Error('useSession must be used inside <SessionGate>')
  return value
}

/** The session, or null on a page anyone opens (a signing link, a job application) */
export const useOptionalSession = (): SessionValue | null => useContext(SessionContext)

/**
 * Whether this app shows one's own things only (Thananan OS, `AppInfo.personal`): its lists ask
 * for `mine`, and work done for others (recording for someone, overviews) is left to the work apps
 */
export function usePersonal(): boolean {
  const { appId } = useSession()
  return !!apps[appId].personal
}

/**
 * The pages of one app in Thananan OS's one router (`/tms/…`): the session as that app sees it — its id (`usePersonal`,
 * links from its own root: `useNavigate` in app/scope.tsx), and NoAccess for someone who may not open it (`gate`; the
 * search box and the like only borrow the id)
 */
export function AppScope({ appId, open, gate = true, children }: { appId: AppId; open?: boolean; gate?: boolean; children: ReactNode }) {
  const outer = useSession()
  const value = useMemo(() => ({ ...outer, appId }), [outer, appId])
  const allowed = !gate || open || opensApp(appId, { ...outer.me, ability: outer.ability })
  return <SessionContext.Provider value={value}>{allowed ? children : <NoAccess />}</SessionContext.Provider>
}

/** `can('create', 'tms.shipment')`, or against a record for `@own` / `@branch` rules: `can('cancel', 'tms.cashTransfer', record)` */
export function useCan() {
  const ability = useAbility<AppAbility>()
  return (action: string, subject: string, record?: Record<string, unknown>) =>
    ability.can(action, record ? tag(subject, { ...record }) : subject)
}

/** The signed-in person's permissions, for rules that take the ability itself (`cancellable`) */
export function useAppAbility() {
  return useAbility<AppAbility>()
}

export { Can } from '@casl/react'

export interface SessionGateProps {
  appId: AppId
  /** The portal is open to every signed-in user */
  open?: boolean
  children: ReactNode
}

/**
 * Loads the signed-in user, builds their permissions and guards the app: its PIN first (Pin.tsx), over the app once it
 * has been open on this page, so what was on screen is still there after the PIN.
 */
export function SessionGate({ appId, open, children }: SessionGateProps) {
  const client = useQueryClient()
  const location = useLocation()
  const { data: me, error, isPending, refetch } = useMeQuery()
  const shown = useRef(false)

  useEffect(() => refreshMeOnUnauthorized(client), [client])
  usePinWatch()

  const value = useMemo<SessionValue | null>(() => {
    if (!me) return null
    return {
      me,
      appId,
      ability: defineAbility(me.permissions, { id: me.user.id, branchId: me.branchId }, subjects),
      signOut: async () => {
        await forgetPushSubscription().catch(() => undefined)
        await authClient.signOut()
        client.clear()
        window.location.assign(loginUrl())
      },
    }
  }, [me, client, appId])

  if (isPending) return <Splash />
  if (isApiError(error, 'UNAUTHORIZED')) {
    // To the one sign-in page (Thananan OS's), coming back here afterwards
    const login = loginUrl(location.pathname + location.search)
    return appId === 'portal' ? <Navigate to={login} replace /> : <LeaveFor href={login} />
  }
  if (error || !value) return <ErrorScreen onRetry={() => refetch()} />
  const { pin, viewedBy } = value.me
  const lock = pin && (!pin.set || pin.locked) ? <PinScreen me={value.me} onSignOut={value.signOut} /> : null
  if (lock && !shown.current) return lock
  shown.current = true
  // A super admin looking as someone sees the apps as they do, an app they cannot open too
  const viewing = viewedBy && <ViewAsBadge userId={value.me.user.id} name={value.me.user.name} until={viewedBy.until} />
  if (!open && !opensApp(appId, { ...value.me, ability: value.ability })) {
    return (
      <SessionContext.Provider value={value}>
        <NoAccess />
        {viewing}
        {lock && <PinOver>{lock}</PinOver>}
      </SessionContext.Provider>
    )
  }
  return (
    <SessionContext.Provider value={value}>
      <AbilityProvider value={value.ability}>
        <div style={{ display: 'contents' }} inert={!!lock}>
          {children}
        </div>
        {viewing}
        {lock && <PinOver>{lock}</PinOver>}
      </AbilityProvider>
    </SessionContext.Provider>
  )
}
